import { useCallback, useContext, useState } from 'react';
import { UserContext } from 'context/User/UserContext';
import { useMsal } from '@azure/msal-react';
import { clearSession } from 'store/utility';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

export const LoginPolicyGate = ({ children }) => {
    // This component is a placeholder for any future login policy checks we may want to implement.
    const { userInfo } = useContext(UserContext);
    const { instance } = useMsal();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const upn =
        userInfo?.userProfile?.UPN ||
        userInfo?.userProfile?.upn ||
        'unknown';

    const logout = useCallback(async () => {
        if (isLoggingOut) {
            return;
        }

        setIsLoggingOut(true);

        try {
            await clearSession();
        } catch {
        }

        try {
            await instance.logoutRedirect();
        } catch {
            try {
                await instance.logoutPopup();
            } catch {
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
            }
        } finally {
            setIsLoggingOut(false);
        }
    }, [instance, isLoggingOut]);

    if (!userInfo?.userProfile) {
        return null;
    }

    if (userInfo.userProfile.Primary) {
        return <>{children}</>;
    }

    return (
        <Dialog
            open
            disableEscapeKeyDown
            onClose={() => {}}
            aria-labelledby="account-restriction-title"
            PaperProps={{
                sx: {
                    maxWidth: 720,
                    width: '100%',
                    // use CSS custom property for a subtle border in the theme color
                    border: '2px solid var(--mdc-theme-primary)',
                },
            }}
        >
            <DialogTitle
                id="account-restriction-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    // use the primary theme CSS variable for the title background
                    backgroundColor: 'var(--mdc-theme-primary)',
                    color: '#fff', // ensure readable text on primary background
                    py: 1.25,
                }}
            >
                <WarningAmberOutlined
                    sx={{ color: 'var(--mdc-theme-secondary)' }}
                />
                Account Restriction
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ py: 1 }}>
                    <Typography component="div">
                        You have logged in with a non-primary account{' '}
                        <strong>{upn}</strong>. Requests in IMS must be
                        submitted with your primary account only.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="contained"
                    onClick={logout}
                    disabled={isLoggingOut}
                    sx={{
                        backgroundColor: 'var(--mdc-theme-primary)',
                        color: '#fff',
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: 'var(--mdc-theme-secondary)',
                        },
                    }}
                >
                    {isLoggingOut
                        ? 'Signing out...'
                        : 'Sign out and switch account'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
